import shortUuid from "short-uuid";
import seedrandom from "seedrandom";
import Vector from "victor";
import * as Sentry from "@sentry/browser";

import { Tool } from "./types/Tool";
import ToolType from "./types/ToolType";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";
import { WBSelection } from "./types/WBSelection";
import DrawingDOMRepresentation from "./DrawingDOMRepresentation";
import { UserInfo } from "./types/UserInfo";
import PanTool from "./otherTools/PanTool";
import {
  LineTool,
  RectTool,
  DiamondTool,
  EllipseTool,
  ArrowTool,
} from "./standardShapes/StandardShapeTools";
import { StickyTool } from "./standardShapes/StickyTool";
import Camera from "./Camera";
import { PenTool, EraserTool, MarkerTool } from "./pen/PenTool";
import {
  WebsocketController,
  WebsocketControllerDelegate,
} from "./WebsocketController";
import { RenderConfig } from "@/store/modules/toolConfig";
import { WBHUDState } from "@/store/WBHUDState";
import SelectTool from "./selection/SelectTool";
import {
  OperationManager,
  WBOperationManagerState,
} from "./operations/OperationManager";
import { Operation } from "./operations/Operation";
import TextTool from "./text/TextTool";
import { postData, postJSON } from "@/utils/post";
import {
  ShapeStore,
  SubjectMessageAny,
  SubjectMessageType,
} from "./ShapeStore";
import KefirBus from "./KefirBus";
import { ImageRenderer } from "./ImageRenderer";
import { Scope } from "./types/Scope";
import { Verb } from "./types/Verb";
import { OldEvent } from "./types/WBEvent";
import { WBEventOuterServer } from "./types/WBEvent2";
import {
  ImageUploadController,
  ImageUploadControllerDelegate,
} from "./ImageUploadController";
import AddImagesOperation from "./operations/AddImagesOperation";
import { NodeShape } from "./standardShapes/NodeShape";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import Delta from "quill-delta";
import { isInIframe } from "./util";
import { PDFController, PDFInfo } from "./PDFController";
import { applyArrangement, ArrangementAnchor } from "./arrangements";
import { StampTool } from "./standardShapes/StampTool";

function makeDiv(className: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = className;
  return el;
}

function preventDefault(e: TouchEvent): void {
  e.preventDefault();
}

const toolTypeToInstance: Partial<Record<ToolType, Tool>> = {
  [ToolType.Pan]: new PanTool(),
  [ToolType.Pen]: new PenTool(),
  [ToolType.Marker]: new MarkerTool(),
  [ToolType.Eraser]: new EraserTool(),
  [ToolType.Line]: new LineTool(),
  [ToolType.Rect]: new RectTool(),
  [ToolType.Diamond]: new DiamondTool(),
  [ToolType.Ellipse]: new EllipseTool(),
  [ToolType.Arrow]: new ArrowTool(),
  [ToolType.Select]: new SelectTool(),
  [ToolType.Text]: new TextTool(),
  [ToolType.Sticky]: new StickyTool(),
  [ToolType.Stamp]: new StampTool(),
};

function doesToolWrite(toolType: ToolType): boolean {
  switch (toolType) {
    case ToolType.Pan:
      return false;
    case ToolType.Pen:
    case ToolType.Marker:
    case ToolType.Eraser:
    case ToolType.Line:
    case ToolType.Rect:
    case ToolType.Diamond:
    case ToolType.Ellipse:
    case ToolType.Arrow:
    case ToolType.Select:
    case ToolType.Text:
    case ToolType.Sticky:
      return true;
    case undefined:
    default:
      return false;
  }
}

export interface WhiteboardControllerDelegate {
  onToolChanged: (toolType: ToolType) => void;
  onUsersChanged: (map: Record<string, UserInfo>) => void;
  onSelectionsChanged: (selections: WBSelection[]) => void;
  onUndoStateChanged: (state: WBOperationManagerState) => void;
  onOfflineStateChanged: (isOffline: boolean) => void;
  onIsLockedChanged: (isLocked: boolean) => void;
  onTriedToUploadImageButDisallowed: () => void;
  onRequestInsertPDF: (info: PDFInfo) => void;
}

export class WhiteboardController
  implements
    WhiteboardControllerInterface,
    WebsocketControllerDelegate,
    ImageUploadControllerDelegate
{
  drawing: DrawingDOMRepresentation;
  websocketController!: WebsocketController;
  operationManager: OperationManager;
  imageUploadController?: ImageUploadController;

  backgroundImageEl: HTMLImageElement | null;
  clientID = shortUuid().new();
  camera: Camera;
  tool?: Tool;
  settings: WBHUDState;
  shapeStore: ShapeStore;
  toolStack = new Array<Tool>();
  toolChangesBus = new KefirBus<ToolType, void>(
    "WhiteboardController.toolChanges"
  );

  transformRoot: HTMLDivElement;
  pdfController = new PDFController();

  getRandom: () => number;

  /** DateTime at which we last call the `whiteboard/{id}/touch` endpoints */
  private lastTouchCallTime = 0;
  private isLocked = false;

  // MARK: Lifecycle

  constructor(
    public el: HTMLDivElement,
    public wbid: string,
    public session: {
      image_upload_capability:
        | "unmoderated"
        | "approved"
        | "banned"
        | "disallowed_anonymous";
    },
    private accessKey: {
      id: string;
      accessLevel: "admin" | "view" | "contribute";
    },
    private isAnonymous: boolean,
    private username: string,
    private websocketServerURL: string,
    backgroundImage: null | {
      image: HTMLImageElement;
      width: number;
      height: number;
    },
    hudState: WBHUDState,
    private renderConfig: RenderConfig,
    private delegate: WhiteboardControllerDelegate
  ) {
    // debugging

    (window as any).wb = this; // eslint-disable-line @typescript-eslint/no-explicit-any

    // Phase 1: bind properties

    this.camera = new Camera(new Vector(el.clientWidth, el.clientHeight));
    this.getRandom = seedrandom(this.clientID);
    this.settings = hudState;

    // Phase 2: create services

    this.shapeStore = new ShapeStore(this.clientID, this);
    this.operationManager = new OperationManager(
      this.shapeStore,
      this.delegate.onUndoStateChanged
    );
    function canEditBoard(accessLevel: string): boolean {
      return accessLevel !== "view";
    }
    if (!isInIframe() && canEditBoard(this.accessKey.accessLevel)) {
      this.imageUploadController = new ImageUploadController(
        this.wbid,
        this.accessKey.id,
        this.camera,
        this.pdfController,
        session.image_upload_capability,
        this
      );
    }

    // Phase 3: configure DOM
    this.transformRoot = makeDiv("WBTransformRoot");
    this.el.appendChild(this.transformRoot);

    if (backgroundImage) {
      this.backgroundImageEl = backgroundImage.image;
      // this.backgroundImageEl = document.createElement("img");
      // this.backgroundImageEl.src = backgroundImage.image.src;
      this.backgroundImageEl.width = backgroundImage.width;
      this.backgroundImageEl.height = backgroundImage.height;
      this.backgroundImageEl.style.left = `${-backgroundImage.width / 2}px`;
      this.backgroundImageEl.style.top = `${-backgroundImage.height / 2}px`;
      this.backgroundImageEl.className = "WBBackgroundImage";
      this.transformRoot.appendChild(this.backgroundImageEl);
      this.camera.panAndZoomToImageBounds(this.backgroundImageEl);
    } else {
      this.backgroundImageEl = null;
    }

    window.addEventListener("resize", this.onResize);

    // Phase 4: set up drawing

    this.drawing = new DrawingDOMRepresentation(
      this,
      this.camera,
      this.transformRoot,
      this.renderConfig
    );

    this.applyCamera();

    // Phase 5: apply state

    if (this.accessKey.accessLevel === "view") {
      this.setTool(new PanTool());
    } else {
      this.setToolFromString(this.settings.tool);
    }

    (window as { wb?: WhiteboardController }).wb = this;

    this.shapeStore.clearEvents.stream.onValue(this.respondToClear);
    this.shapeStore.undoClearEvents.stream.onValue(this.respondToUndoClear);
    this.shapeStore.userChangeEvents.stream.onValue(this.touchLastModified);
    this.shapeStore.changes.onValue(this.onShapeStoreChange);
    this.camera.allChanges.onValue(this.applyCamera);
    this.drawing.focusedTextShapeIDBus.stream.onValue(this.onTextFocusChanged);

    // Phase 6: begin interactivity

    this.imageUploadController?.bind(this.el);

    this.bindWebsocketController(
      new WebsocketController(
        this.websocketServerURL,
        this.wbid,
        this.clientID,
        this.accessKey.id,
        this
      )
    );

    this.el.addEventListener("touchmove", preventDefault);
  }

  bindWebsocketController(wc: WebsocketController) {
    this.websocketController = wc;
    this.websocketController.onlineStream.onValue(
      this.delegate.onOfflineStateChanged
    );
    this.shapeStore.replaceWebsocketController(wc);
    this.websocketController.eventStream.onValue(this.onWebsocketEvent);
    this.websocketController.connect();
  }

  get loadingPromise(): Promise<void> {
    return this.websocketController.loadingPromise.then(() => {
      this.drawing.finishLoad();
      setTimeout(this.startThumbnailUpload, 0);
    });
  }

  get canWrite(): boolean {
    return (
      this.accessKey.accessLevel === "admin" ||
      (this.accessKey.accessLevel === "contribute" && !this.isLocked)
    );
  }

  unmount() {
    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: "WhiteboardController.unmount",
      level: Sentry.Severity.Info,
    });
    // potentially temporary unmount; no need to reset data
    window.removeEventListener("resize", this.onResize);
    this.el.removeEventListener("touchmove", preventDefault);
    if (this.backgroundImageEl) {
      this.transformRoot.removeChild(this.backgroundImageEl);
    }
    Array.from(this.el.children).forEach((element) => {
      this.el.removeChild(element);
    });
    this.websocketController.teardown();
    this.shapeStore.clearEvents.stream.offValue(this.respondToClear);
    this.shapeStore.undoClearEvents.stream.offValue(this.respondToUndoClear);
    this.shapeStore.userChangeEvents.stream.offValue(this.touchLastModified);
    this.shapeStore.changes.offValue(this.onShapeStoreChange);
    this.websocketController.eventStream.offValue(this.onWebsocketEvent);
    this.websocketController.onlineStream.offValue(
      this.delegate.onOfflineStateChanged
    );
    this.drawing.focusedTextShapeIDBus.stream.offValue(this.onTextFocusChanged);
    this.shapeStore.teardown();
    this.imageUploadController?.unbind();
  }

  updateUsername(username: string) {
    this.username = username;
    this.websocketController.refreshUserData();
  }

  requestSetIsLocked(value: boolean) {
    this.websocketController.publish({
      id: this.makeID(),
      wbid: this.wbid,
      clientID: this.clientID,
      scope: Scope.Persist,
      payload: {
        verb: Verb.SetIsLocked,
        isLocked: value,
      },
    });
    this.isLocked = value;
    this.delegate.onIsLockedChanged(value);
  }

  onTextFocusChanged = (id: string | null) => {
    if (id === null) return;
    // Nothing to do, really
  };

  onWebsocketEvent = (msg: WBEventOuterServer | OldEvent) => {
    const payload = (msg as WBEventOuterServer).payload;
    if (!payload) return;
    switch (payload.verb) {
      case Verb.SetIsLocked:
        this.isLocked = payload.isLocked;
        if (
          !this.canWrite &&
          this.tool?.toolType &&
          doesToolWrite(this.tool?.toolType)
        ) {
          this.setToolFromString(ToolType.Pan);
        }
        this.delegate.onIsLockedChanged(this.isLocked);
        break;
      default:
        break;
    }
  };

  onShapeStoreChange(msg: SubjectMessageAny) {
    // Special select tool behaviors to prevent users from clobbering each other's changes
    const selectTool = toolTypeToInstance[ToolType.Select] as SelectTool;
    if (!selectTool) return;
    const selection = selectTool.findSelectionForShape(msg.subjectID);

    if (!selection) return; // not updating anything selected

    // If anyone else is editing this, remove it from the selection
    switch (msg.type) {
      case SubjectMessageType.Delete:
        selectTool.removeFromSelections(selection);
        break;
      case SubjectMessageType.Upsert:
        if (msg.isFromMyself) return;
        selectTool.removeFromSelections(selection);
        break;
      default:
        break;
    }
  }

  render = (
    scaleFactor = 1,
    transparentBackground = true,
    updateProgress?: (frac: number) => void
  ) => {
    return new ImageRenderer(
      this.drawing,
      scaleFactor,
      transparentBackground
    ).renderToDataURL(updateProgress);
  };

  thumbnailUploadHandle?: unknown;
  scheduleThumbnailUpload() {
    this.cancelThumbnailUpload();
    this.thumbnailUploadHandle = setTimeout(this.startThumbnailUpload, 3000);
  }
  cancelThumbnailUpload() {
    if (this.thumbnailUploadHandle) {
      clearTimeout(this.thumbnailUploadHandle as number);
      this.thumbnailUploadHandle = undefined;
    }
  }

  startThumbnailUpload = () => {
    // Don't upload thumbnails on anonymous boards or if the user isn't an admin
    if (this.isAnonymous || this.accessKey.accessLevel !== "admin") return;

    this.cancelThumbnailUpload();

    this.doThumbnailUpload();
  };

  async doThumbnailUpload() {
    const canvas = await new ImageRenderer(
      this.drawing,
      1,
      false
    ).renderToCanvas(true, true);
    if (!canvas.width || !canvas.height) return;

    const canvas2 = document.createElement("canvas");
    canvas2.width = 640;
    canvas2.height = 480;
    const ctx = canvas2.getContext("2d");
    if (!ctx) return;
    ctx.translate(canvas2.width / 2, canvas2.height / 2);
    const scale = Math.min(
      canvas2.width / canvas.width,
      canvas2.height / canvas.height
    );
    ctx.scale(scale, scale);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
    ctx.drawImage(canvas, 0, 0);
    canvas2.toBlob(async (blob) => {
      if (!blob) {
        console.error("Could not render image");
        return;
      }
      try {
        const response = await postJSON<{
          url: string;
          fields: Record<string, string>;
        }>(
          `/whiteboards/${this.wbid}/upload_thumbnail?content_length=${blob.size}`,
          {}
        );
        // console.log(response);
        // await postFile(response.data.url, "file", blob, response.data.fields);
        await postData(response.data.url, blob, "image/png");
      } catch (e) {
        console.warn("Could not upload thumbnail");
        console.error(e);
      }
    });
  }

  /**
   * Kill the websocket controller and the drawing so we re-fetch all data from
   * the server and render all the shapes.
   *
   * This is in response to an undo-clear event, where we probably don't have
   * all the events we need to render without a full reload.
   */
  respondToUndoClear = () => {
    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: "WhiteboardController.resetEventStream",
      level: Sentry.Severity.Info,
    });
    if (this.drawing) {
      this.drawing.teardown();
    }
    this.shapeStore.reset();
    this.drawing = new DrawingDOMRepresentation(
      this,
      this.camera,
      this.transformRoot,
      this.renderConfig
    );
    this.websocketController.teardown();
    this.bindWebsocketController(
      new WebsocketController(
        this.websocketServerURL,
        this.wbid,
        this.clientID,
        this.accessKey.id,
        this
      )
    );
  };

  refreshUserData() {
    this.websocketController.refreshUserData();
  }

  // MARK: External API

  applyOperation(op: Operation) {
    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: `applyOperation-${op.constructor.name}`,
      level: Sentry.Severity.Info,
    });
    op.applyForward(this.shapeStore);
    this.operationManager.addOperation(op);
  }

  undo() {
    this.operationManager.undo(this);
  }

  redo() {
    this.operationManager.redo(this);
  }

  setIsShiftDown(value: boolean) {
    if (this.tool?.toolType === ToolType.Select) {
      (this.tool as SelectTool)?.setIsShiftDown(value);
    }
  }

  pushTool(toolType: ToolType) {
    const newTool = toolTypeToInstance[toolType];
    if (!newTool) {
      throw new Error("Can't push a no-op tool");
      return;
    }
    if (this.tool?.toolType === toolType) return;
    if (
      this.toolStack.length &&
      this.toolStack[this.toolStack.length - 1].toolType === toolType
    )
      return;
    if (this.tool) this.toolStack.push(this.tool);
    this.setTool(newTool);
    // console.log(this.toolStack);
  }

  popTool() {
    if (!this.toolStack.length) return;
    // eslint-disable-next-line
    this.setTool(this.toolStack.pop()!);
  }

  popToolType(t: ToolType): void {
    if (this.tool?.toolType === t) {
      this.popTool();
    }
  }

  setToolFromString(s: ToolType) {
    if (this.tool?.toolType === s) return;
    const newTool = toolTypeToInstance[s];
    if (newTool) {
      this.setTool(newTool);
    } else {
      console.error(`Unknown tool type: ${s}`);
      return;
    }
    this.toolStack = [];
  }

  settingsDidChange(settings: WBHUDState) {
    this.settings = settings;
    this.tool?.settingsDidChange(settings);
    this.websocketController.refreshUserData();
  }

  updateNextZIndex(nextZIndex: number) {
    this.tool?.nextZIndexDidChange(nextZIndex);
  }

  setTool(t: Tool) {
    if (t === this.tool) return;
    console.log("Set tool", t);
    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: `changeTool-${t.toolType}`,
      level: Sentry.Severity.Info,
    });
    this.transformRoot.className = `WBTransformRoot m-tool-${t.toolType}`;
    this.tool?.unbind(this);
    this.tool = t;
    if (t.toolType !== ToolType.Text) {
      this.drawing.focusTextShape(null, "tool change");
    }
    this.tool.settingsDidChange(this.settings);
    this.tool.nextZIndexDidChange(this.drawing.nextZIndex);
    this.tool.bind(this);
    this.toolChangesBus.emit(t.toolType);
    this.delegate.onToolChanged(t.toolType);
    if (t.toolType === ToolType.Select) {
      (t as SelectTool).notifySelectionsChanged();
    } else {
      this.delegate.onSelectionsChanged([]);
    }
  }

  clear = () => {
    Sentry.addBreadcrumb({
      category: "Whiteboard",
      message: `clear`,
      level: Sentry.Severity.Info,
    });
    this.shapeStore.send(Scope.Persist, { verb: Verb.Clear });
    this.operationManager.reset();
  };

  respondToClear = () => {
    // nothing to do
  };

  deleteSelections = () => {
    (this.tool as SelectTool)?.deleteAll();
  };

  scrollUsingEvent(e: WheelEvent) {
    e.preventDefault();
    this.camera.setOrigin(
      new Vector(
        this.camera.origin.x + this.camera.scale * e.deltaX,
        this.camera.origin.y + this.camera.scale * e.deltaY
      )
    );
  }

  zoomUsingEvent(e: WheelEvent) {
    e.preventDefault();
    this.camera.setScale(
      Math.min(
        Math.max(
          0.125,
          this.camera.scale + e.deltaY * -0.005 * this.camera.scale
        ),
        4
      )
    );
  }

  duplicateSelectedShapes() {
    if (this.tool === this.selectTool) this.selectTool.duplicateSelections();
  }

  beginPlacingImageFromLibrary = (image: { url: string; size: Vector }) => {
    this.setToolFromString(ToolType.Stamp);
    this.stampTool.prepare(image);
  };

  // MARK: Internal API

  get selectTool(): SelectTool {
    return toolTypeToInstance[ToolType.Select] as SelectTool;
  }

  get textTool(): TextTool {
    return toolTypeToInstance[ToolType.Text] as TextTool;
  }

  get panTool(): PanTool {
    return toolTypeToInstance[ToolType.Pan] as PanTool;
  }

  get stampTool(): StampTool {
    return toolTypeToInstance[ToolType.Stamp] as StampTool;
  }

  makeID(): string {
    return shortUuid().new();
  }

  private applyCamera = () => {
    this.transformRoot.style.transform = this.camera.transformString();
  };

  selectionsDidChange(selections: WBSelection[]) {
    this.delegate.onSelectionsChanged(selections);
  }

  selectStickyAndBeginEditing(shapeID: string) {
    if (this.tool?.toolType !== ToolType.Select) {
      this.setTool(this.selectTool);
    }
    this.drawing.focusTextShape(shapeID, "sticky tool");
  }

  private touchLastModified = () => {
    if (this.isAnonymous || this.accessKey.accessLevel !== "admin") return;
    const now = new Date().getTime();
    if (now - this.lastTouchCallTime < 60000) {
      return;
    }
    postJSON(`/whiteboards/${this.wbid}/touch`, { key: this.accessKey }).catch(
      () => {
        /* does't matter */
      }
    );
    this.lastTouchCallTime = now;
  };

  // MARK: Event handlers

  onResize = () => {
    this.camera.setViewportSize(
      new Vector(this.el.clientWidth, this.el.clientHeight)
    );
    if (this.backgroundImageEl) {
      this.camera.panAndZoomToImageBounds(this.backgroundImageEl);
    }
  };

  // MARK: WebsocketControllerDelegate

  websocketDidHaveError() {
    this.delegate.onOfflineStateChanged(true);
  }

  websocketDidResolveError() {
    this.delegate.onOfflineStateChanged(false);
  }

  websocketDidChangeUsers(map: Record<string, UserInfo>) {
    if (!this.delegate.onUsersChanged) return;
    this.delegate.onUsersChanged(map);
  }

  getUsername(): string {
    return this.username;
  }

  getColor(): string {
    return this.settings.strokeColor;
  }

  // MARK: ImageUploadControllerDelegate

  imageUploadControllerDidReceiveImage(
    images: { size: Vector; url: string; position: Vector }[]
  ): void {
    this.insertImages(images);
  }

  imageUploadControllerDidReceivePDF(pdf: PDFInfo) {
    this.insertPDF(pdf.url);
  }

  imageUploadControllerDidReceiveDisallowedUploadRequest(): void {
    this.delegate.onTriedToUploadImageButDisallowed();
  }

  insertImages(images: { size: Vector; url: string; position: Vector }[]) {
    const nextZIndex = this.drawing.nextZIndex;
    const newIDs = images.map(() => this.makeID());
    const op = new AddImagesOperation(
      newIDs,
      images.map((image, i) => makeImageShape(image, nextZIndex + i))
    );
    this.applyOperation(op);
    this.updateNextZIndex(nextZIndex + images.length);
    this.setTool(this.selectTool);
    this.selectTool.replaceSelections(
      this.drawing.orderedShapes.filter((s) => newIDs.indexOf(s.id) !== -1)
    );
  }

  insertPDF(url: string) {
    this.pdfController.load(url, 1).then((info) => {
      this.delegate.onRequestInsertPDF(info);
    });
  }

  finishInsertingPDF(
    info: PDFInfo,
    pageStart: number,
    pageEnd: number,
    scale: number,
    orientation: "horizontal" | "vertical"
  ) {
    const nextZIndex = this.drawing.nextZIndex;
    const numPages = pageEnd - pageStart + 1; // inclusive and 1-indexed
    const newIDs: string[] = [...Array(numPages)].map(() => this.makeID());
    const shapes = [...Array(numPages)].map((_, i) =>
      makePDFShape(
        info,
        pageStart + i,
        this.camera.origin,
        scale,
        nextZIndex + i
      )
    );
    const arrangedShapes = applyArrangement(
      shapes,
      orientation === "horizontal" ? ArrangementAnchor.W : ArrangementAnchor.N
    );

    const op = new AddImagesOperation(newIDs, arrangedShapes);

    this.applyOperation(op);
    this.updateNextZIndex(nextZIndex + 1);
    this.setTool(this.selectTool);
    this.selectTool.replaceSelections(
      newIDs.map((id) => this.drawing.indexedShapes[id])
    );
  }
}

function makePDFShape(
  info: PDFInfo,
  pageNumber: number,
  center: Vector,
  scale: number,
  zIndex: number
): NodeShape {
  const origin = center
    .clone()
    .subtract(info.size.clone().divide(new Vector(2, 2)));
  return {
    shapeType: ShapeType.Node,
    aspectRatio: info.size.x / info.size.y,
    imageURL: info.url,
    pageNumber,
    fillColor: "rgba(0, 0, 0, 0)",
    strokeColor: "rgba(0, 0, 0, 0)",
    strokeWidth: 0,
    zIndex,
    text: new Delta(),
    translate: [0, 0],
    boundingBoxOrigin: origin.toArray() as [number, number],
    boundingBoxSize: info.size.clone().multiplyScalar(scale).toArray() as [
      number,
      number
    ],
    boundingBoxOriginalSize: info.size.toArray() as [number, number],
    pointA: [0, 0],
    pointB: info.size.toArray() as [number, number],
  };
}

function makeImageShape(
  image: { size: Vector; url: string; position: Vector },
  zIndex: number
): NodeShape {
  const origin = image.position
    .clone()
    .subtract(image.size.clone().divide(new Vector(2, 2)));
  return {
    shapeType: ShapeType.Node,
    aspectRatio: image.size.x / image.size.y,
    imageURL: image.url,
    fillColor: "rgba(0, 0, 0, 0)",
    strokeColor: "rgba(0, 0, 0, 0)",
    strokeWidth: 0,
    zIndex,
    text: new Delta(),
    translate: [0, 0],
    boundingBoxOrigin: origin.toArray() as [number, number],
    boundingBoxSize: image.size.clone().toArray() as [number, number],
    boundingBoxOriginalSize: image.size.clone().toArray() as [number, number],
    pointA: [0, 0],
    pointB: image.size.clone().toArray() as [number, number],
  };
}
